import { Injectable, inject } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Params,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable, from, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";

import { CountryCodeEnum } from "src/app/shared/models/country.model";
import {
  LANDING_ROUTE_CAMPAIGN_PARAM,
  LANDING_ROUTE_COUNTRY_PARAM,
  LANDING_ROUTE_PATH,
} from "src/app/shared/models/landing.model";

import { LandingService } from "src/app/landing/services/landing.service";
import { UserService } from "src/app/shared/services/user.service";
import { StorageService } from "src/app/shared/services/storage.service";
import { AuthService } from "src/app/shared/services/auth.service";
import { RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService } from "src/app/experiments/redirect-authenticated-users-away-from-landing-page/redirect-authenticated-users-away-from-landing-page.service";
import {
  AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH,
  getAuthenticatedLandingRedirectPath,
  shouldRedirectAuthenticatedLandingUser$,
} from "src/app/guards/authenticated-landing-redirect";
import { User } from "src/app/shared/models/user.model";

/**
 * Validates landing route access and prepares locale state before page activation.
 * It canonicalizes country routes and preloads the landing translation bundle
 * for the final route country.
 */
@Injectable({ providedIn: "root" })
export class LandingGuard {
  private readonly router = inject(Router);
  private readonly landingService = inject(LandingService);
  private readonly userService = inject(UserService);
  private readonly storageService = inject(StorageService);
  private readonly authService = inject(AuthService);
  private readonly redirectAuthenticatedLandingUsersExperiment = inject(
    RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService
  );

  /**
   * Validates landing route state before activation.
   *
   * @param route Current route snapshot.
   * @param _state Current router state snapshot (unused, kept for guard signature consistency).
   * @returns `true` to continue activation or a redirect UrlTree.
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    const routeCountry = this.getRouteCountry(route);
    const routeCampaign = this.getRouteCampaign(route);
    if (!routeCountry) {
      // Parent landing route has no :country yet; child guard will handle.
      return of(true);
    }

    return shouldRedirectAuthenticatedLandingUser$(
      this.redirectAuthenticatedLandingUsersExperiment,
      this.authService,
      this.storageService
    ).pipe(
      switchMap((shouldRedirectAuthenticatedUser) => {
        if (shouldRedirectAuthenticatedUser) {
          return this.redirectToAuthenticatedDestination$();
        }

        return this.resolveLandingRoute(
          routeCountry,
          routeCampaign,
          route.queryParams
        );
      }),
      catchError(() =>
        this.resolveLandingRoute(routeCountry, routeCampaign, route.queryParams)
      )
    );
  }

  private redirectToMain(): UrlTree {
    this.redirectAuthenticatedLandingUsersExperiment.markRedirectedToMainAfterEnabledFlag();
    return this.router.createUrlTree([
      AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH,
    ]);
  }

  private redirectToAuthenticatedDestination$(): Observable<UrlTree> {
    return from(this.storageService.get("user") as Promise<User | null>).pipe(
      take(1),
      map((user) => {
        const path = getAuthenticatedLandingRedirectPath(user);
        this.applyAuthenticatedUserCountryBeforeOnboardingRedirect(user, path);
        return path === AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH
          ? this.redirectToMain()
          : this.router.createUrlTree([path]);
      }),
      catchError(() => of(this.redirectToMain()))
    );
  }

  private applyAuthenticatedUserCountryBeforeOnboardingRedirect(
    user: User | null,
    path: string
  ): void {
    const profileCountry = user?.country;
    if (
      path === AUTHENTICATED_LANDING_MAIN_REDIRECT_PATH ||
      !profileCountry ||
      !Object.values(CountryCodeEnum).includes(profileCountry)
    ) {
      return;
    }

    this.userService.setCountry(profileCountry, user?.language || "");
  }

  /**
   * Resolves existing landing canonicalization and translation preload behavior.
   *
   * @param routeCountry Original route country param.
   * @param routeCampaign Optional campaign param from the route tree.
   * @param queryParams Existing route query parameters.
   * @returns `true` to continue activation or a canonical landing redirect.
   */
  private resolveLandingRoute(
    routeCountry: string,
    routeCampaign: string | null,
    queryParams: Params
  ): Observable<boolean | UrlTree> {
    return this.getAuthenticatedProfileCountry().pipe(
      map((profileCountry) => {
        if (profileCountry) {
          const normalizedRouteCountry = routeCountry.toLowerCase();
          const normalizedProfileCountry = profileCountry.toLowerCase();
          const isCrossCountry =
            normalizedRouteCountry !== normalizedProfileCountry;
          const needsCasingFix = routeCountry !== normalizedProfileCountry;

          return {
            resolvedCountry: profileCountry,
            shouldRedirect: isCrossCountry || needsCasingFix,
            preserveCampaign: Boolean(routeCampaign) && !isCrossCountry,
          };
        }

        const resolvedCountry =
          this.landingService.resolveCountryWithFallback(routeCountry);

        return {
          resolvedCountry,
          shouldRedirect: this.shouldRedirectToCanonicalCountry(
            routeCountry,
            resolvedCountry
          ),
          preserveCampaign:
            Boolean(routeCampaign) &&
            this.landingService.isValidCountry(routeCountry),
        };
      }),
      switchMap(({ resolvedCountry, shouldRedirect, preserveCampaign }) => {
        if (shouldRedirect) {
          return of(
            this.buildLandingUrlTree(
              resolvedCountry,
              queryParams,
              preserveCampaign ? routeCampaign : null
            )
          );
        }

        return this.landingService.preloadTranslationsForCountry(
          resolvedCountry
        );
      })
    );
  }

  /**
   * Determines whether current route country should be canonicalized.
   *
   * @param routeCountry Original route param value.
   * @param resolvedCountry Normalized and validated country.
   * @returns `true` when redirect to canonical route is required.
   */
  private shouldRedirectToCanonicalCountry(
    routeCountry: string | null,
    resolvedCountry: CountryCodeEnum
  ): boolean {
    if (!this.landingService.isValidCountry(routeCountry)) {
      return true;
    }

    return routeCountry !== resolvedCountry.toLowerCase();
  }

  /**
   * Resolves country param from current route tree.
   * Supports both parent-level and child-level `:country` placement.
   *
   * @param route Current activated route snapshot.
   * @returns Raw country value when present; otherwise `null`.
   */
  private getRouteCountry(route: ActivatedRouteSnapshot): string | null {
    const directCountry = route.paramMap.get(LANDING_ROUTE_COUNTRY_PARAM);
    if (directCountry) {
      return directCountry;
    }

    const childCountry = route.firstChild?.paramMap.get(
      LANDING_ROUTE_COUNTRY_PARAM
    );

    if (childCountry) {
      return childCountry;
    }

    return null;
  }

  /**
   * Resolves campaign param from current route tree.
   *
   * @param route Current activated route snapshot.
   * @returns Raw campaign value when present; otherwise `null`.
   */
  private getRouteCampaign(route: ActivatedRouteSnapshot): string | null {
    const directCampaign = route.paramMap.get(LANDING_ROUTE_CAMPAIGN_PARAM);
    if (directCampaign) {
      return directCampaign;
    }

    return route.firstChild?.paramMap.get(LANDING_ROUTE_CAMPAIGN_PARAM) ?? null;
  }

  /**
   * Builds landing UrlTree while preserving query parameters.
   *
   * @param country Redirect target country.
   * @param queryParams Existing route query parameters.
   * @returns Redirect UrlTree.
   */
  private buildLandingUrlTree(
    country: CountryCodeEnum,
    queryParams: Params,
    campaign: string | null
  ): UrlTree {
    const path = campaign
      ? `${LANDING_ROUTE_PATH}/${country.toLowerCase()}/${campaign}`
      : `${LANDING_ROUTE_PATH}/${country.toLowerCase()}`;

    return this.router.createUrlTree([path], { queryParams });
  }

  /**
   * Resolves the authenticated user's profile country, if available.
   *
   * Looks first at the in-memory user profile subject (when already loaded),
   * then falls back to storage for a persisted user profile.
   *
   * @returns Observable emitting the normalized profile country or `null`.
   */
  private getAuthenticatedProfileCountry(): Observable<CountryCodeEnum | null> {
    const profileCountry = this.getProfileCountryFromSubject();
    if (profileCountry) {
      return of(profileCountry);
    }

    return from(Promise.resolve(this.storageService.get("user"))).pipe(
      map((user) => {
        if (!user?.userId) {
          return null;
        }

        return this.normalizeProfileCountry(user?.country);
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Reads profile country from the user profile subject if the user is authenticated.
   *
   * @returns Normalized profile country when available; otherwise `null`.
   */
  private getProfileCountryFromSubject(): CountryCodeEnum | null {
    const user = this.userService.userProfileSubject?.value?.user;
    if (!user?.userId) {
      return null;
    }

    const profileCountry = user.country;
    return this.normalizeProfileCountry(profileCountry);
  }

  /**
   * Normalizes a raw country value into a supported enum value.
   *
   * @param country Raw country value from profile/storage.
   * @returns Normalized `CountryCodeEnum` or `null` when unsupported.
   */
  private normalizeProfileCountry(country: unknown): CountryCodeEnum | null {
    if (typeof country !== "string") {
      return null;
    }

    const normalized = country.toUpperCase();

    return this.landingService.isValidCountry(normalized)
      ? (normalized as CountryCodeEnum)
      : null;
  }
}
